import { type MobileOrder, orderRetrieve, orgUnitTableRetrieve, type TableOrder } from "@innoclouds/api";
import { computedAsync, StorageSerializers, useLocalStorage } from "@vueuse/core";
import axios from "axios";
import i18next from "i18next";
import { defineStore } from "pinia";
import { computed, ref, type WatchCallback, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { getWebSocketInstance, type WebSocketService } from "../utils/WebSocketService";
import { useMainStore } from "./main";
import { useModulesStore } from "./modules";
import { useOrgUnitStore } from "./org-unit";
import { useScheduleStore } from "./schedule";

const IGNORE_WS_TYPES = [`ping`, `pong`];
const IGNORE_REFRESH_TABLE_STATUSES = [`confirmed`, `waiting_payment`, `partial_paid`];
const DELETED_STATUSES = [`cancelled`];
const NEW_ORDER_SELECTION = "__new__";

export const useTableStore = defineStore("table-data", () => {
	const route = useRoute();
	const router = useRouter();
	const mainStore = useMainStore();
	const moduleStore = useModulesStore();
	const scheduleStore = useScheduleStore();
	const orgUnit = useOrgUnitStore();

	const version = ref(1);
	const orderVersion = ref(1);
	const _appMode = useLocalStorage<string | null>("appMode", null, { shallow: true });
	const tablePresister = useLocalStorage<TableOrder | null>("tableData", null, { shallow: true, serializer: StorageSerializers.object });
	const _tableId = useLocalStorage<string | null>("tableId", null);
	// null = не выбрано (берём первый заказ), NEW_ORDER_SELECTION = гость явно начал новый заказ, иначе — id заказа
	const _orderSelection = useLocalStorage<string | null>("tableOrderId", null);

	const shouldPromptOrders = ref(false);

	const requestOrdersPrompt = () => {
		shouldPromptOrders.value = true;
		_orderSelection.value = null;
	};

	const tableId = computed({
		get: () => data.value?.id,
		set: (v) => {
			_tableId.value = v;
			if (v) requestOrdersPrompt();
		},
	});
	const defaultTable = computed(() => mainStore.config_lib?.default_table);
	const fallback = computed(() => route.query.table_id?.toString());

	const requestedTableId = computed(() => fallback.value || _tableId.value || defaultTable.value);
	const isTableLoaded = computed(() => !!data.value?.id && data.value.id === requestedTableId.value);

	const isTableService = computed(() => data.value?.branch.work_mode === "table-service");
	const isTableTent = computed(() => _appMode.value === "table-tent");
	const isOrgUnitLoaded = computed(() => !!orgUnit.data || orgUnit.isError);
	const allowMultipleOrders = computed(() => !!orgUnit.data?.allow_multiple_orders_on_table);

	const orders = computed(() => data.value?.orders ?? []);
	const isNewOrder = computed(() => _orderSelection.value === NEW_ORDER_SELECTION);
	const selectedOrder = computed(() => {
		if (isNewOrder.value) return null;
		return orders.value.find((item) => item.id === _orderSelection.value) ?? orders.value[0] ?? null;
	});
	const hasOrder = computed(() => isTableService.value && !!selectedOrder.value);
	const availableExtend = computed(() => selectedOrder.value?.available_extend ?? false);

	const consumeOrdersPrompt = () => {
		shouldPromptOrders.value = false;
	};

	const selectOrder = (id: string | null) => {
		_orderSelection.value = id;
		consumeOrdersPrompt();
	};

	const startNewOrder = () => {
		_orderSelection.value = NEW_ORDER_SELECTION;
		consumeOrdersPrompt();
	};

	let savedTableData: TableOrder | null = null;
	let ws: WebSocketService | null = null;

	// @TODO: вынести в composable и на vue-query после миграции на wildcard?
	const data = computedAsync(async (onCancel) => {
		version.value;
		const abortController = new AbortController();
		onCancel(() => abortController.abort());

		if (!orgUnit.id || !mainStore.config_lib?.area) return null;
		const id = requestedTableId.value;
		if (!id) return null;

		try {
			const result = await orgUnitTableRetrieve(orgUnit.id, id, { signal: abortController.signal });
			return { ...result, __v: version.value };
		} catch (e: any) {
			if (axios.isCancel(e)) throw e;
			if (axios.isAxiosError(e) && (e.code === "ERR_NETWORK" || e.message === "Network Error")) throw e;
			if (id !== defaultTable.value) _tableId.value = null;
			// if (e.response.status === 503) _tableId.value = null;
			// Решение плохое, но выкручиваемся как можем, хотя обновление страницы все поломает
			if (isTableTent.value) return tablePresister.value;
			if (id === savedTableData?.id) return savedTableData;
			savedTableData = null;
			// @tolgee-key { key: 'table-service.notification.table-error', ns: 'translation' }
			moduleStore.showNotification(e?.response?.data?.user_friendly_message || i18next.t(`table-service.notification.table-error`));
			return null;
		}
	}, null);

	// @TODO: вынести в composable и на vue-query после миграции на wildcard?
	const order = computedAsync(async (onCancel) => {
		orderVersion.value;
		const abortController = new AbortController();
		onCancel(() => abortController.abort());
		if (!isTableService.value || !selectedOrder.value) return null;

		try {
			const result = await orderRetrieve(selectedOrder.value.id, { signal: abortController.signal });
			const items = result.items.map((item) => ({ ...item, idSystem: crypto.randomUUID() }));

			return { ...result, items, __v: orderVersion.value };
		} catch (e) {
			if (axios.isCancel(e)) throw e;
			if (axios.isAxiosError(e) && (e.code === "ERR_NETWORK" || e.message === "Network Error")) throw e;

			return null;
		}
	}, null);

	const registerWebSocketListeners = (orderData: MobileOrder | null) => {
		if (ws) {
			ws.close();
			ws = null;
		}
		if (!orderData) return;

		ws = getWebSocketInstance(`${import.meta.env.VITE_WS_URL}/ws/order/${orderData.id}/`);
		ws.on("message", (data) => {
			if (IGNORE_WS_TYPES.includes(data?.type)) return;
			if (data?.method === "update_order_status" && DELETED_STATUSES.includes(data.params.status)) {
				// @tolgee-key { key: 'table-service.notification.order-cancelled', ns: 'translation' }
				moduleStore.showNotification(i18next.t(`table-service.notification.order-cancelled`));
			}

			refreshOrder();
		});
		ws.connect();
	};

	const refresh = () => {
		version.value++;
	};

	const refreshOrder = () => {
		orderVersion.value++;
	};

	i18next.on("languageChanged", () => {
		version.value++;
		orderVersion.value++;
	});

	const presist: WatchCallback<TableOrder | null> = (data) => {
		if (!data) return;
		savedTableData = data;
		if (isTableTent.value) tablePresister.value = data;
		if (_tableId.value !== data.id) {
			_tableId.value = data.id;
			_orderSelection.value = null;
		}
		if (_orderSelection.value && _orderSelection.value !== NEW_ORDER_SELECTION && !data.orders?.some((item) => item.id === _orderSelection.value))
			_orderSelection.value = null;
		if (data?.branch?.schedule) scheduleStore.set(data.branch.schedule);
	};

	const onOrderUpadted: WatchCallback<MobileOrder | null, MobileOrder | null | undefined> = (newOrder, oldOrder) => {
		if (newOrder?.id !== oldOrder?.id) registerWebSocketListeners(newOrder);
		if (newOrder && !IGNORE_REFRESH_TABLE_STATUSES.includes(newOrder.status as any)) refresh();
		if (!newOrder && oldOrder && !isNewOrder.value && oldOrder.table?.id === data.value?.id)
			return router.replace({ name: "order-ts-status", params: { id: oldOrder.id } });
	};

	watch(order, onOrderUpadted, { immediate: true });
	watch(data, presist, { immediate: true });
	watch(fallback, (queryTableId) => queryTableId && requestOrdersPrompt(), { immediate: true });

	return {
		id: tableId,
		isTableService,
		isTableTent,
		isOrgUnitLoaded,
		allowMultipleOrders,
		hasOrder,
		orders,
		selectedOrder,
		isNewOrder,
		isTableLoaded,
		shouldPromptOrders,
		availableExtend,
		selectOrder,
		startNewOrder,
		consumeOrdersPrompt,
		data,
		order,
		refresh,
		refreshOrder,
	};
});
